import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  assignmentOf,
  getDexieStore,
  getLocalDb,
  landedPenaltyEntries,
  liveQuery,
  mergePenaltyView,
  penaltyTotal,
  reconcilePenalties,
  type LandedPenalty,
  type OutboxEntry,
  type PenaltyAddPayload,
  type PenaltyCode,
  type PenaltyRemovePayload,
  type PenaltyViewRow,
  type ServerPenaltyRow,
} from '@flm9/sync';
import { api } from './api';
import { pruneLandedForRoster, recordLandedPenalties, watchLanded } from './landedPenalties';
import { wakeRefereeSync } from './sync';

export type PenaltyValues = Partial<Record<PenaltyCode, number>>;

/** Penalty points as shown on buttons and in lists: "−10", "+5", "0". */
export function formatPenaltyPoints(points: number): string {
  if (points < 0) return `−${Math.abs(points)}`;
  if (points > 0) return `+${points}`;
  return '0';
}

/** Penalty rows as liveAssignments delivers them (key/note optional on old servers). */
interface InitialPenalty {
  id: string;
  code: PenaltyCode;
  points: number;
  note?: string | null;
  clientIdempotencyKey?: string | null;
}

function normalize(rows: readonly InitialPenalty[]): ServerPenaltyRow[] {
  return rows.map((p) => ({
    id: p.id,
    code: p.code,
    points: p.points,
    note: p.note ?? null,
    clientIdempotencyKey: p.clientIdempotencyKey ?? null,
  }));
}

const isPenaltyEntry = (e: OutboxEntry) => e.op === 'penalty.add' || e.op === 'penalty.remove';

/**
 * The shooter's penalties for the score screen: server rows (liveAssignments
 * props, refreshed via penalties.listByAssignment) merged with the unresolved
 * outbox entries (`mergePenaltyView`).
 *
 * When a queued entry lands (acked) it leaves the outbox before the server
 * rows are refetched. To keep the row (and the totals) from blinking, every
 * uploaded penalty change is recorded as "landed" (Dexie meta, written by the
 * drainer before the ack — see landedPenalties.ts) and applied on top of the
 * server rows. On screen, a refetch started after it landed supersedes it;
 * the record itself stays until the roster (`initial`) shows it, because a
 * remount (e.g. back from Timer-Debug) starts from that roster again. A
 * queued add dropped by "Entfernen" before it was ever sent never landed and
 * is not recorded.
 */
export function usePenalties({
  assignmentId,
  initial,
  values,
}: {
  assignmentId: string;
  initial: readonly InitialPenalty[];
  values: PenaltyValues | undefined;
}) {
  const [server, setServer] = useState<ServerPenaltyRow[]>(() => normalize(initial));
  useEffect(() => {
    setServer(normalize(initial));
  }, [initial]);

  const [entries, setEntries] = useState<OutboxEntry[]>([]);
  const [landed, setLanded] = useState<LandedPenalty[]>([]);
  // Start of the newest refetch whose rows are shown (0 = only the roster).
  const [freshSince, setFreshSince] = useState(0);
  const seq = useRef(0);

  // The roster caught up → forget the landed changes it shows.
  useEffect(() => {
    if (landed.length === 0) return;
    pruneLandedForRoster(assignmentId, normalize(initial)).catch(() => undefined);
  }, [assignmentId, initial, landed]);

  const refresh = useCallback(async () => {
    const mine = ++seq.current;
    const startedAt = Date.now();
    try {
      const rows = await api.penaltiesByAssignment(assignmentId);
      if (!Array.isArray(rows)) return;
      // Rejected entries that did land after all must stop blocking the queue.
      const store = getDexieStore();
      const before = await store.listFailedTerminal();
      const acked = await reconcilePenalties(assignmentId, rows);
      if (acked > 0) {
        // They did land — keep them visible until the roster has them.
        const still = new Set((await store.listFailedTerminal()).map((e) => e.id));
        await recordLandedPenalties(
          before.filter((e) => !still.has(e.id) && assignmentOf(e) === assignmentId),
        );
        wakeRefereeSync();
      }
      if (mine !== seq.current) return;
      setServer(normalize(rows));
      setFreshSince(startedAt);
    } catch (err) {
      // Offline / expired login: keep showing what we have.
      console.warn('penalty refresh deferred', err);
    }
  }, [assignmentId]);

  useEffect(() => {
    setEntries([]);
    setLanded([]);
    setFreshSince(0);
    const stopLanded = watchLanded(assignmentId, setLanded);
    let prev = new Map<string, OutboxEntry>();
    let first = true;
    const db = getLocalDb();
    const sub = liveQuery(() =>
      db.outbox
        .where('status')
        .anyOf('pending', 'inflight', 'failed_terminal')
        .filter((e) => isPenaltyEntry(e) && assignmentOf(e) === assignmentId)
        .toArray(),
    ).subscribe({
      next: (rows) => {
        setEntries(rows);
        const now = new Map(rows.map((e) => [e.id, e]));
        const gone = first ? [] : [...prev.keys()].filter((id) => !now.has(id));
        prev = now;
        first = false;
        // An entry left the outbox → fetch the rows that should show it.
        if (gone.length > 0) void refresh();
      },
      error: (err) => console.error('penalty outbox query failed', err),
    });
    const onOnline = () => void refresh();
    // Reconcile when the screen opens online (like the shot reconcile).
    if (navigator.onLine) void refresh();
    window.addEventListener('online', onOnline);
    return () => {
      sub.unsubscribe();
      stopLanded();
      window.removeEventListener('online', onOnline);
    };
  }, [assignmentId, refresh]);

  const rows: PenaltyViewRow[] = useMemo(() => {
    const base = new Map(server.map((p) => [p.clientIdempotencyKey ?? p.id, p]));
    const unseen = landedPenaltyEntries(landed.filter((l) => l.landedAt > freshSince));
    for (const entry of unseen.sort((a, b) => a.createdAt - b.createdAt)) {
      if (entry.op === 'penalty.add') {
        const p = entry.payload as PenaltyAddPayload;
        if (!base.has(p.clientIdempotencyKey))
          base.set(p.clientIdempotencyKey, {
            id: `local:${p.clientIdempotencyKey}`,
            code: p.code,
            points: values?.[p.code] ?? 0,
            note: p.note ?? null,
            clientIdempotencyKey: p.clientIdempotencyKey,
          });
      } else {
        base.delete((entry.payload as PenaltyRemovePayload).clientIdempotencyKey);
      }
    }
    return mergePenaltyView(assignmentId, [...base.values()], entries, values);
  }, [assignmentId, server, landed, freshSince, entries, values]);

  const total = useMemo(() => penaltyTotal(rows), [rows]);

  return { rows, total, refresh };
}
